<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Username Search Input -->
              <base-input
                col="12"
                type="text"
                :placeholder="$t('PLACEHOLDERS.search_by_username')"
                v-model.trim="filterOptions.username"
              />
              <!-- End:: Username Search Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.contest_members") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <button @click="$router.go(-1)" class="back_btn">
            <i class="fal fa-arrow-left mx-2"></i>
            {{ $t("BUTTONS.back") }}
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Member Image -->
        <template v-slot:[`item.avatar`]="{ item }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.avatar">
              {{ $t("TABLES.noData") }}
            </h6>
            <button v-else class="my-1" @click="showImageModal(item.avatar)">
              <img
                class="rounded-circle"
                :src="item.avatar"
                :alt="item.name"
                width="60"
                height="60"
                style="object-fit: cover"
              />
            </button>
          </div>
        </template>
        <!-- End:: Member Image -->

        <!-- Start:: Member Name -->
        <template v-slot:[`item.name`]="{ item }">
          <div>
            <h6 class="text-danger" v-if="!item.name">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else class="mb-0">{{ item.name }}</p>
          </div>
        </template>
        <!-- End:: Member Name -->

        <!-- Start:: Steps Count -->
        <template v-slot:[`item.steps`]="{ item }">
          <div>
            <h6
              class="text-danger"
              v-if="item.steps === null || item.steps === undefined"
            >
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else class="mb-0 font-weight-bold">
              {{ item.steps.toLocaleString() }}
            </p>
          </div>
        </template>
        <!-- End:: Steps Count -->

        <!-- Start:: Rank -->
        <template v-slot:[`item.progress.rank`]="{ item }">
          <div>
            <h6
              class="text-danger"
              v-if="!item.progress || !item.progress.rank"
            >
              {{ $t("TABLES.noData") }}
            </h6>
            <div
              v-else
              class="d-flex align-items-center justify-content-center"
            >
              <span
                class="rank_badge"
                :class="{
                  rank_gold: item.progress.rank === 1,
                  rank_silver: item.progress.rank === 2,
                  rank_bronze: item.progress.rank === 3,
                }"
              >
                #{{ item.progress.rank }}
              </span>
            </div>
          </div>
        </template>
        <!-- End:: Rank -->

        <!-- Start:: Block Status -->
        <template v-slot:[`item.is_blocked`]="{ item }">
          <div class="d-flex justify-content-center">
            <span
              :class="item.is_blocked ? 'badge bg-danger' : 'badge bg-success'"
            >
              {{
                item.is_blocked
                  ? $t("PLACEHOLDERS.blocked")
                  : $t("PLACEHOLDERS.active")
              }}
            </span>
          </div>
        </template>
        <!-- End:: Block Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- Start:: Toggle Block -->
            <a-tooltip
              placement="bottom"
              v-if="$can('contests edit', 'contests')"
            >
              <template slot="title">
                <span>{{
                  item.is_blocked ? $t("BUTTONS.unblock") : $t("BUTTONS.block")
                }}</span>
              </template>
              <button
                :class="item.is_blocked ? 'btn_show' : 'btn_delete'"
                @click="toggleBlockMember(item)"
              >
                <i
                  :class="item.is_blocked ? 'fal fa-lock-open' : 'fal fa-ban'"
                ></i>
              </button>
            </a-tooltip>
            <!-- End:: Toggle Block -->

            <!-- Start:: Delete Member -->
            <a-tooltip
              placement="bottom"
              v-if="$can('contests delete', 'contests')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.remove_member") }}</span>
              </template>
              <button class="btn_delete" @click="selectDeleteMember(item)">
                <i class="fal fa-user-times"></i>
              </button>
            </a-tooltip>

            <template v-else>
              <i
                class="fal fa-lock-alt fs-5 blue-grey--text text--darken-1"
              ></i>
            </template>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Image Modal -->
          <image-modal
            v-if="dialogImage"
            :modalIsOpen="dialogImage"
            :modalImage="selectedItemImage"
            @toggleModal="dialogImage = !dialogImage"
          />
          <!-- End:: Image Modal -->

          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title
                class="text-h5 justify-center"
                v-if="memberToDelete"
              >
                {{
                  $t("TITLES.RemoveMemberConfirmingMessage", {
                    name: memberToDelete.name,
                  })
                }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteMember">{{
                  $t("BUTTONS.ok")
                }}</v-btn>
                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!--  =========== Start:: Pagination =========== -->
      <template>
        <div class="pagination_container text-center mt-3 mb-0">
          <v-pagination
            v-model="paginations.current_page"
            :length="paginations.last_page"
            :total-visible="6"
            @input="updateRouterQueryParam($event)"
            color="deep-purple darken-4"
          ></v-pagination>
        </div>
      </template>
      <!--  =========== End:: Pagination =========== -->
    </main>
    <!-- End:: Main Section -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ContestMembers",

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      // Start:: Loader Control Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        username: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: "#",
          value: "id",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.member_image"),
          value: "avatar",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.member_name"),
          value: "name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.steps_count"),
          value: "steps",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.rank"),
          value: "progress.rank",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "is_blocked",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogImage: false,
      selectedItemImage: null,
      dialogDelete: false,
      memberToDelete: null,
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
        total: 0,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: `/contests/${this.id}/members`,
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.username = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: `/contests/${this.id}/members`,
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Data
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `contests/${this.id}/members`,
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.username,
          },
        });
        this.loading = false;

        // Set table rows
        this.tableRows = res.data.data.data;

        // Set pagination data
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
        this.paginations.total = res.data.data.meta.total;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(
          error.response?.data?.message ||
            this.$t("MESSAGES.someThingWentWrong")
        );
      }
    },
    // End:: Set Table Rows

    // Start:: Control Modals
    showImageModal(image) {
      this.dialogImage = true;
      this.selectedItemImage = image;
    },
    // End:: Control Modals

    // Start:: Toggle Block Member
    async toggleBlockMember(item) {
      try {
        await this.$axios({
          method: "POST",
          url: `contests/${this.id}/members/${item.id}/toggle-block`,
        });
        this.setTableRows();
        this.$message.success(
          item.is_blocked
            ? this.$t("MESSAGES.memberUnblockedSuccessfully")
            : this.$t("MESSAGES.memberBlockedSuccessfully")
        );
      } catch (error) {
        this.$message.error(
          error.response?.data?.message ||
            this.$t("MESSAGES.someThingWentWrong")
        );
      }
    },
    // End:: Toggle Block Member

    // ==================== Start:: Crud ====================
    // ===== Start:: Delete Member
    selectDeleteMember(item) {
      this.dialogDelete = true;
      this.memberToDelete = item;
    },

    async confirmDeleteMember() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `contests/${this.id}/members/${this.memberToDelete.id}`,
        });
        this.dialogDelete = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.memberRemovedSuccessfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(
          error.response?.data?.message ||
            this.$t("MESSAGES.someThingWentWrong")
        );
      }
    },
    // ===== End:: Delete Member
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style lang="scss" scoped>
.back_btn {
  padding: 8px 16px;
  background: #7514b7;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #5c1090;
  }
}

.contest_info_wrapper {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.rank_badge {
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 16px;
}

.rank_gold {
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  color: #000;
}

.rank_silver {
  background: linear-gradient(135deg, #c0c0c0, #e8e8e8);
  color: #000;
}

.rank_bronze {
  background: linear-gradient(135deg, #cd7f32, #e9a76e);
  color: #fff;
}
</style>
